import type { PDFDocumentProxy } from "pdfjs-dist";

// Sheet numbers as drawing sets write them: a discipline letter or two, then
// digits, as in A-101, A101, S2.01, M-201A, E0.1 or FP-001.
const SHEET_NUMBER = /^([A-Z]{1,3})[-. ]?\d{1,4}(?:\.\d{1,3})?[A-Z]?$/;

// title block labels shaped the same way, as in "REV 2" or "NO. 3"
const NOT_SHEET_PREFIXES = new Set(["NO", "REV", "RV", "PG", "DWG", "DT"]);

const IsSheetNumber = (text: string) => {
  const match = SHEET_NUMBER.exec(text);
  return match !== null && !NOT_SHEET_PREFIXES.has(match[1]);
};

// how many pages are read between updates to the panels
const PAGES_PER_UPDATE = 20;

interface PdfSheetCandidate {
  sheetNumber: string;
  // the text's height, in PDF units; a title block's sheet number is the
  // largest text in it
  size: number;
}

/**
 * Reads each page's sheet number from the text in its title block, so the
 * viewer can show and find sheets by number. A page whose title block is a
 * picture, as in a scanned set, has no text to read and is left without one.
 */
export class PdfSheetReader {
  private onChange: (
    sheetNumbers: { [pageNumber: number]: string },
    done: boolean,
  ) => void;
  // a new document stops the reading of the one before it
  private readCount = 0;

  constructor(
    onChange: (
      sheetNumbers: { [pageNumber: number]: string },
      done: boolean,
    ) => void,
  ) {
    this.onChange = onChange;
  }

  async Read(pdfDocument: PDFDocumentProxy) {
    const readCount = ++this.readCount;
    // every page's candidates, best first
    const candidates: PdfSheetCandidate[][] = [];

    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
      try {
        candidates.push(await this.ReadPage(pdfDocument, pageNumber));
      } catch (error) {
        // a destroyed document ends the reading; a bad page is skipped
        if (readCount !== this.readCount) return;
        console.error("Error reading sheet number:", error);
        candidates.push([]);
      }
      if (readCount !== this.readCount) return;

      if (
        pageNumber % PAGES_PER_UPDATE === 0 ||
        pageNumber === pdfDocument.numPages
      ) {
        this.onChange(
          PickSheetNumbers(candidates),
          pageNumber === pdfDocument.numPages,
        );
      }
    }
  }

  Cancel() {
    this.readCount++;
  }

  private async ReadPage(
    pdfDocument: PDFDocumentProxy,
    pageNumber: number,
  ): Promise<PdfSheetCandidate[]> {
    const page = await pdfDocument.getPage(pageNumber);
    // turned as the page is meant to be read, so "bottom right" is where a
    // reader sees it
    const viewport = page.getViewport({ scale: 1 });
    const textContent = await page.getTextContent();

    // A title block's text is often split into pieces, "A-" and "101", so
    // pieces on one line in one size are joined before they are read.
    const runs: { text: string; x: number; y: number; size: number }[] = [];
    for (const item of textContent.items as any[]) {
      if (typeof item.str !== "string" || !item.transform) continue;

      const [x, y] = viewport.convertToViewportPoint(
        item.transform[4],
        item.transform[5],
      );
      const size = Math.hypot(item.transform[2], item.transform[3]);
      const last = runs[runs.length - 1];
      if (
        last &&
        Math.abs(last.y - y) < size * 0.5 &&
        Math.abs(last.size - size) < size * 0.1 &&
        x >= last.x &&
        item.str.trim() !== ""
      ) {
        last.text += item.str;
      } else if (item.str.trim() !== "") {
        runs.push({ text: item.str, x, y, size });
      }
    }
    page.cleanup();

    const pageCandidates: PdfSheetCandidate[] = [];
    for (const { text, x, y, size } of runs) {
      // the title block runs down the right edge or along the bottom right
      const inTitleBlock =
        x > viewport.width * 0.75 ||
        (x > viewport.width * 0.5 && y > viewport.height * 0.8);
      if (!inTitleBlock) continue;

      const value = text.trim().toUpperCase();
      // the number alone, or a label and the number, as in "SHEET A-101"
      const sheetNumber = IsSheetNumber(value)
        ? value
        : value.split(/\s+/).find(IsSheetNumber);
      if (sheetNumber) {
        pageCandidates.push({ sheetNumber, size });
      }
    }

    return pageCandidates.sort((first, second) => second.size - first.size);
  }
}

/**
 * Each page's largest candidate, except for text every page carries in the
 * same place, such as a project number shaped like a sheet number.
 */
const PickSheetNumbers = (
  candidates: PdfSheetCandidate[][],
): { [pageNumber: number]: string } => {
  const pageCounts = new Map<string, number>();
  for (const pageCandidates of candidates) {
    for (const sheetNumber of new Set(
      pageCandidates.map(({ sheetNumber }) => sheetNumber),
    )) {
      pageCounts.set(sheetNumber, (pageCounts.get(sheetNumber) ?? 0) + 1);
    }
  }
  const repeated = (sheetNumber: string) =>
    candidates.length >= 3 &&
    (pageCounts.get(sheetNumber) ?? 0) > candidates.length / 2;

  const sheetNumbers: { [pageNumber: number]: string } = {};
  candidates.forEach((pageCandidates, index) => {
    const pick = pageCandidates.find(
      ({ sheetNumber }) => !repeated(sheetNumber),
    );
    if (pick) {
      sheetNumbers[index + 1] = pick.sheetNumber;
    }
  });

  return sheetNumbers;
};

// "a101", "A 101" and "A-101" all find sheet A-101
export const NormalizeSheetNumber = (sheetNumber: string) =>
  sheetNumber.toUpperCase().replace(/[^A-Z0-9.]/g, "");
